import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { Category } from '@/types'

/**
 * Canonical query key for the shared category list. Exported so mutations can
 * `invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY })` without hardcoding the
 * string in yet another place.
 */
export const CATEGORIES_QUERY_KEY = ['categories'] as const

/**
 * Single source of truth for the shared `['categories']` React Query cache.
 *
 * Why this exists (F-FE-N6): TanStack Query stores ONE value per key. Before
 * this hook, seven components each defined their own `useQuery({ queryKey:
 * ['categories'] })` with subtly different `queryFn`s — six returned the
 * unwrapped `Category[]`, but ItemsTab returned the raw `APIResponse` envelope
 * `{success, message, data}`. Whichever query resolved LAST decided the shape
 * every consumer read, so on a background refetch the cache could flip from an
 * array to an object and `categories.forEach(...)` would crash
 * ("N.forEach is not a function"), white-screening the Reports → Items tab.
 *
 * Funnelling every consumer through this hook makes shape drift structurally
 * impossible: the key is only ever written by this one `queryFn`, which ALWAYS
 * resolves to `Category[]`.
 *
 * `getCategories()` defaults to active-only, matching what every consumer wants.
 */
export function useCategories(
  options?: Omit<UseQueryOptions<Category[], Error, Category[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: () => apiClient.getCategories().then((r) => (r.data as Category[]) ?? []),
    staleTime: 60_000,
    ...options,
  })
}
